// Small shared UI primitives built on theme.js. Presentational only - none of these components
// fetch, validate, or transform data; they receive already-computed props and render them.
//
// Each exists because the audit found the same thing hand-rolled inconsistently across files:
//   NavGroup   - sidebar crowding, no progressive disclosure anywhere (3.1)
//   Field      - `outline: 'none'` with no replacement focus style in ~6 form files (4.2)
//   LoadError  - failed GETs rendered identically to genuine empty states (6.2, 6.4)
//   IconButton - `<div onClick>` controls unreachable by keyboard (4.3)
//   Toggle     - custom switches with no role/aria-checked/keyboard support (4.1b)

import React, { useState, useCallback, useEffect, useId } from 'react';
import { mkT, focusRing, FS, SP, FW, R, btnVariant, BTN_FOCUS, BTN_DISABLED } from './theme';
import { GOLD, DANGER, TEXT_MUTED, TEXT_FAINT } from './colors';
import { IconWarning } from './icons';
import { docText, useDocLang, reportBrowserRefusal } from './docText';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

/** REDESIGN Stage 1: the single button component, 4 variants sharing one geometry.
 *
 *  variant: 'primary' (navy fill) | 'secondary' (outline) | 'danger' (red outline) | 'ghost'.
 *  Hover and focus are handled with local state because inline styles cannot express :hover or
 *  :focus-visible, and this codebase styles exclusively with inline objects. Renders a real
 *  <button> so it is keyboard-reachable and receives focus by default. */
export function Btn({ variant = 'primary', dark, disabled, onClick, style, children, ...rest }) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const v = btnVariant(variant, dark);
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      style={{
        ...v.base,
        ...(hover && !disabled ? v.hover : {}),
        ...(focus ? BTN_FOCUS : {}),
        ...(disabled ? BTN_DISABLED : {}),
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Sidebar group with expand/collapse, persisted per-group in localStorage.
 *  Defaults to EXPANDED, so an existing user sees no change until they collapse something. */
export function NavGroup({ label, groupKey, dark, children, defaultOpen = true }) {
  const storageKey = `enko_nav_${groupKey}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? defaultOpen : v === '1';
    } catch { return defaultOpen; }
  });
  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* private mode */ }
      return next;
    });
  }, [storageKey]);

  const t = mkT(dark);
  return (
    <div>
      <button type="button" onClick={toggle} style={t.navGroup} aria-expanded={open}>
        <span>{label}</span>
        <span aria-hidden="true" style={{
          fontSize: '9px', color: dark ? TEXT_MUTED : TEXT_FAINT,
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s',
          display: 'inline-block',
        }}>▶</span>
      </button>
      {open && children}
    </div>
  );
}

/** LABELFIX (2026-10-22): a VISIBLE label above any form control (input, select, textarea), linked
 *  to it by id, that stays visible while the person types. Wraps the existing control unchanged, so
 *  its value, handlers, placeholder (kept as an example only) and styling stay exactly as they were.
 *  `style` goes on the wrapper, so a control that spans grid columns keeps doing so. */
export function Labeled({ label, dark, style, children }) {
  const autoId = useId();
  const child = React.Children.only(children);
  const controlId = child.props.id || autoId;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, ...style }}>
      <label htmlFor={controlId}
        style={{ fontSize: '12px', fontWeight: 500, lineHeight: 1.3, color: dark ? '#c9d1dd' : '#4a5568' }}>
        {label}
      </label>
      {React.cloneElement(child, { id: controlId })}
    </div>
  );
}

/** Text input with a visible focus ring. Every form file in the app sets `outline: 'none'`
 *  with no replacement, which is a WCAG 2.4.7 failure; inline styles can't express :focus, so
 *  the ring is applied via onFocus/onBlur - the same handler pattern used for hover elsewhere. */
export function Field({ label, dark, style, hint, id, ...inputProps }) {
  const [focused, setFocused] = useState(false);
  const t = mkT(dark);
  // A11YLABEL (audit A1): the label is now EXPLICITLY associated with the input via
  // htmlFor/id, not only implicitly by being its ancestor. The wrapping <label> is kept, so
  // nothing about the layout or the click target changes; this adds the explicit pair that
  // assistive technology and the audit's own check both look for.
  //
  // THE ID COMES FROM useId, SO IT STAYS UNIQUE WHEN THE SAME FORM APPEARS TWICE ON A PAGE
  // (point 1a). A hand-rolled id derived from `name` or `label` would collide the moment a
  // screen rendered two copies of a form, which this app does (a staff view and a self-service
  // view of the same section). React generates one id per component INSTANCE, so two instances
  // of the same Field cannot share one. A caller may still pass an explicit `id` and it wins,
  // which is what lets an existing test or an external <label> target a known value.
  //
  // The hint is tied to the input through aria-describedby for the same reason: a hint that is
  // only visually adjacent is not announced.
  const autoId = useId();
  const inputId = id || `field-${autoId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const describedBy = [inputProps['aria-describedby'], hintId].filter(Boolean).join(' ') || undefined;
  // THE LABEL IS A SIBLING OF THE INPUT, NOT ITS ANCESTOR, AND THAT IS THE WHOLE POINT.
  //
  // The previous shape wrapped everything in one <label>, including the hint. A wrapping label
  // contributes ALL of its text to the control's accessible name, so a field with a hint was
  // announced as "Postcode As it appears on your utility bill" - the hint was read as part of
  // the field's NAME rather than as its description. That is a real defect, and it was found by
  // the A11YFIX test asserting getByLabelText('Postcode'), which could not resolve.
  //
  // Splitting them fixes it: the <label> carries only the field's name and is bound by
  // htmlFor/id, while the hint is bound separately through aria-describedby, which is the
  // attribute whose job is exactly "extra information, announced after the name".
  //
  // The outer <div> keeps `display: block` so the rendered layout is unchanged.
  return (
    <div style={{ display: 'block' }}>
      {label && (
        <label htmlFor={inputId} style={{
          display: 'block', fontSize: FS.small, fontWeight: FW.medium,
          color: dark ? TEXT_MUTED : '#555', marginBottom: SP.xs,
        }}>{label}</label>
      )}
      <input
        {...inputProps}
        id={inputId}
        aria-describedby={describedBy}
        onFocus={(e) => { setFocused(true); inputProps.onFocus?.(e); }}
        onBlur={(e) => { setFocused(false); inputProps.onBlur?.(e); }}
        style={{ ...t.input, ...(focused ? focusRing : {}), ...style }}
      />
      {hint && <span id={hintId} style={{ display: 'block', fontSize: FS.micro, color: dark ? TEXT_MUTED : TEXT_FAINT, marginTop: SP.xs }}>{hint}</span>}
    </div>
  );
}

/** Applies the focus ring to any existing input without restructuring it. For files whose
 *  inputs carry bespoke layout styles that Field would override. Spread onto the element. */
export function useFocusRing(dark) {
  const [focused, setFocused] = useState(false);
  return {
    focused,
    focusProps: { onFocus: () => setFocused(true), onBlur: () => setFocused(false) },
    focusStyle: focused ? focusRing : {},
  };
}

/** Read-failure state. MUST be used instead of an empty-state message when a GET rejects -
 *  the audit's most consequential finding was that the two were indistinguishable, so a failed
 *  load read as "you have nothing pending". */
export function LoadError({ dark, message = "Couldn't load this section.", onRetry }) {
  const t = mkT(dark);
  return (
    <div style={t.loadError} role="alert">
      <IconWarning size={16} />
      <span>{message}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} style={{
          marginLeft: 'auto', background: 'none', border: `1px solid ${DANGER}`,
          color: DANGER, borderRadius: R.sm, padding: '2px 10px',
          fontSize: FS.small, cursor: 'pointer',
        }}>Retry</button>
      )}
    </div>
  );
}

/** KBDACCESS: makes an element that is already click-operable equally operable by keyboard,
 *  for the cases where the element genuinely cannot become a real <button> without changing
 *  how it looks or lays out - a <tr> (a button cannot wrap table cells) and the upload drop
 *  zone (a button would inherit button styling and break the drop target's layout).
 *
 *  Spread onto the element ALONGSIDE its existing onClick, which stays exactly as it was so
 *  mouse behaviour is byte-identical. Adds only: Tab reachability, Enter/Space activation, and
 *  the role/label assistive technology needs to announce it.
 *
 *  Space is preventDefault'ed because its default action is to scroll the page - without that,
 *  activating a row would also jump the viewport. Enter is not, since it has no default here.
 *  The handler ignores events bubbling up from a nested control (a button inside a row), so
 *  pressing Enter on an inner button does not also fire the row's own action. */
export function activateProps(onActivate, { label, role = 'button' } = {}) {
  return {
    role,
    tabIndex: 0,
    ...(label ? { 'aria-label': label } : {}),
    onKeyDown: e => {
      if (e.target !== e.currentTarget) return;   // let a nested control handle its own keys
      if (e.key === 'Enter' || e.key === ' ') {
        if (e.key === ' ') e.preventDefault();     // Space would otherwise scroll the page
        onActivate(e);
      }
    },
  };
}

/** Keyboard-reachable icon button. Replaces `<div onClick>` controls (dark-mode toggle,
 *  topbar logo, delegate banner) which Tab never lands on. */
export function IconButton({ onClick, label, children, style, pressed }) {
  return (
    <button
      type="button" onClick={onClick} aria-label={label} title={label}
      {...(pressed !== undefined ? { 'aria-pressed': pressed } : {})}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', ...style }}
    >
      {children}
    </button>
  );
}

/** Accessible on/off switch. The custom toggles in EmployeeProfile are nested <span>s with no
 *  role, no aria-checked and no key handling - fully unusable by keyboard, despite controlling
 *  visa/permit status. Visual appearance here matches the existing toggles. */
export function Toggle({ checked, onChange, label, disabled, dark }) {
  return (
    <button
      type="button" role="switch" aria-checked={!!checked} aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: '38px', height: '20px', borderRadius: R.full, position: 'relative',
        backgroundColor: checked ? GOLD : (dark ? '#3a4a6b' : '#ccc'),
        border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1, padding: 0, transition: 'background-color 0.15s',
        flexShrink: 0,
      }}
    >
      <span aria-hidden="true" style={{
        position: 'absolute', top: '2px', left: checked ? '20px' : '2px',
        width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#fff',
        transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      }} />
    </button>
  );
}

/** CONFIRMMODAL: the app's ONE confirmation dialog, replacing every native window.confirm()
 *  (unstyled browser popup, inconsistent with the rest of the product). Visuals follow the
 *  established COMPMODAL/DocumentUpload overlay pattern: fixed rgba(0,0,0,0.5) backdrop,
 *  centred card radius 12, role="dialog" aria-modal, backdrop click cancels. Buttons are the
 *  shared Btn (danger variant for destructive confirmations), so a destructive action never
 *  looks more inviting than Cancel. Purely presentational: the caller owns the action. */
export function ConfirmModal({ open, dark, title = 'Please confirm', message,
                               confirmLabel = 'Confirm', cancelLabel = 'Cancel',
                               danger = false, onConfirm, onCancel, extra = null }) {
  if (!open) return null;
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label={title}
        style={{
          backgroundColor: dark ? '#131c2e' : '#fff',
          color: dark ? '#eaecf0' : '#1a1a2e',
          borderRadius: '12px', padding: '24px', width: '420px', maxWidth: 'calc(100vw - 48px)',
          boxShadow: '0 12px 16px -4px rgba(16,24,40,.18), 0 4px 6px -2px rgba(16,24,40,.08)',
          border: dark ? '1px solid #25324c' : 'none',
        }}
      >
        <h2 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 600 }}>{title}</h2>
        <p style={{ margin: '0 0 20px 0', fontSize: FS.body, lineHeight: 1.5, color: dark ? '#b6bdc9' : '#444', whiteSpace: 'pre-line' }}>
          {message}
        </p>
        {/* FIRSTLOGIN: optional extra content (e.g. an opt-in checkbox), between text and buttons. */}
        {extra}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SP.sm }}>
          <Btn variant="secondary" dark={dark} onClick={onCancel}>{cancelLabel}</Btn>
          <Btn variant={danger ? 'danger' : 'primary'} dark={dark} onClick={onConfirm}>{confirmLabel}</Btn>
        </div>
      </div>
    </div>
  );
}

/** Companion hook so a call site converts from `if (!window.confirm(msg)) return; act();` to
 *  `askConfirm({ message: msg, danger: true, onConfirm: act })` with a single rendered
 *  {confirmModal} in the component tree. Confirm closes the modal first, then runs the action -
 *  identical semantics to the synchronous confirm() it replaces. */
export function useConfirm(dark) {
  const [req, setReq] = useState(null);
  const askConfirm = useCallback((opts) => setReq(opts), []);
  const confirmModal = req ? (
    <ConfirmModal
      open
      dark={dark}
      title={req.title}
      message={req.message}
      confirmLabel={req.confirmLabel}
      cancelLabel={req.cancelLabel}
      danger={req.danger}
      extra={req.extra || null}
      onCancel={() => setReq(null)}
      onConfirm={() => { const run = req.onConfirm; setReq(null); if (run) run(); }}
    />
  ) : null;
  return [askConfirm, confirmModal];
}


/** INTERNVIS: a small "Intern" tag shown next to an employee name in list views. Renders nothing
 *  for non-interns, so call sites can drop it inline unconditionally: <InternBadge employee={emp} />.
 *  Reuses the same inline-pill shape the codebase already uses (see Dashboard's visaPill), in a
 *  neutral gold tint to distinguish it from the critical/red visa pill. When the term is known it
 *  appends "Short"/"Long" so a COO can tell a 6-week intern from a 6-month one at a glance. */
export function InternBadge({ employee, style }) {
  const type = employee && (employee.employee_type || employee.type);
  if (type !== 'intern') return null;
  const term = employee.intern_term;
  const termLabel = term === 'short_term' ? 'Short' : term === 'long_term' ? 'Long' : null;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        fontSize: '11px', fontWeight: 600, padding: '1px 7px',
        borderRadius: R.full, whiteSpace: 'nowrap', marginLeft: '6px',
        backgroundColor: 'rgba(200,168,75,0.16)', color: GOLD,
        ...style,
      }}
    >
      Intern{termLabel ? ` · ${termLabel}` : ''}
    </span>
  );
}

/** ACCESSWAIT: how long a new starter has been waiting for their portal access, shown next to
 *  their name in the new starters lists. Renders NOTHING when nothing is owed, so call sites can
 *  drop it in unconditionally exactly like <InternBadge employee={emp} /> - the pattern this
 *  deliberately follows rather than inventing a second one.
 *
 *  THE PROBLEM IT SURFACES: since WELCOMEDELAY, the welcome email is sent five hours after
 *  Section 2 is submitted. If Section 2 is never submitted, the company mailbox is never created,
 *  the welcome email has nowhere to go, and the person simply never receives credentials. Until
 *  now they sat in this list looking like everyone else. Measured in the live data before
 *  building this: one real employee had been waiting ten days having never logged in.
 *
 *  ITEM 6, THE MIGRATED CASE: an employee migrated from the vendor system ALREADY has a working
 *  company mailbox and legitimately never submits Section 2, so nothing is owed and they are NOT
 *  waiting. They render nothing at all. Both migration flags are checked because either one alone
 *  marks that path, matching the `hideSections` guard the COO list already applies one line away.
 *
 *  ESCALATION THRESHOLD is a WEEK for the visual (this component) and 14 days for the CEO email
 *  (scheduler.job_access_wait_escalation). They are deliberately different: the list should show
 *  concern before anyone is emailed about it, so the COO has a chance to act on their own.
 */
export const ACCESS_WAIT_WARN_DAYS = 7;

export function accessWaitDays(employee, form, now = new Date()) {
  if (!employee || !employee.created_at) return null;
  // Migrated: mailbox already exists, no Section 2 owed, never "waiting".
  if (employee.is_migrated || employee.migration_confirmed) return null;
  // Section 2 done, or the welcome already sent: the wait is over either way.
  if (form && (form.section2_completed || form.welcome_email_sent_at)) return null;
  const created = new Date(employee.created_at);
  if (isNaN(created.getTime())) return null;
  const days = Math.floor((now - created) / 86400000);
  return days < 0 ? 0 : days;
}

export function AccessWait({ employee, form, style, now }) {
  const days = accessWaitDays(employee, form, now);
  if (days === null) return null;
  // Past a week this stops being informational and becomes a warning, in the same critical red
  // the overdue pills elsewhere already use. Below it, a quiet neutral count.
  const warn = days >= ACCESS_WAIT_WARN_DAYS;
  return (
    <span
      title={warn
        ? `No portal access for ${days} days. Section 2 (IT setup) has never been submitted, so no welcome email could be sent.`
        : `Waiting ${days} day${days === 1 ? '' : 's'} for Section 2 (IT setup).`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        fontSize: '11px', fontWeight: 600, padding: '1px 7px',
        borderRadius: R.full, whiteSpace: 'nowrap', marginLeft: '6px',
        backgroundColor: warn ? 'rgba(166,58,58,0.12)' : 'rgba(0,0,0,0.05)',
        color: warn ? '#A63A3A' : '#777',
        ...style,
      }}
    >
      {warn ? `No access · ${days}d` : `Waiting ${days}d`}
    </span>
  );
}

/** CAREERTL: the career timeline - an employee's role periods, newest first.
 *
 *  Renders ONLY entries that genuinely exist. There is deliberately no backfill anywhere in this
 *  feature, so an employee with no entries has simply had no tracked change since the timeline
 *  shipped - which is NOT the same as having no career history. Saying that plainly is the whole
 *  point of the empty state: a bare "no history" would actively mislead.
 *
 *  Two tracks (entry_type 'employee_type' and 'job_title') are shown in one chronological list,
 *  each labelled, so a reader sees "became permanent" and "became Senior Analyst" in the order
 *  they happened rather than as two disconnected lists.
 */
export function CareerTimeline({ entries, dark, loading, error, onRetry, trackingSince }) {
  const t = mkT(dark);
  if (loading) {
    return <p style={{ fontSize: FS.small, color: TEXT_MUTED, fontStyle: 'italic', margin: 0 }}>Loading...</p>;
  }
  if (error) {
    return <LoadError dark={dark} message="Couldn't load the career timeline." onRetry={onRetry} />;
  }
  if (!entries || entries.length === 0) {
    return (
      <p style={{ fontSize: FS.small, color: TEXT_MUTED, margin: 0, lineHeight: 1.5 }}>
        No role changes recorded yet.{trackingSince ? ` Tracking started on ${trackingSince}.` : ''}
        {' '}Entries appear here from the next job title change or employment type conversion.
      </p>
    );
  }

  const LABEL = { employee_type: 'Employment type', job_title: 'Job title' };
  const fmt = (d) => (d ? toDisplayDate(d).toLocaleDateString() : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SP.md }}>
      {entries.map((e, i) => (
        <div key={e.id} style={{ display: 'flex', gap: SP.md, alignItems: 'flex-start' }}>
          {/* Rail: a dot per period, gold for the current one, with a connecting line. */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, paddingTop: '4px' }}>
            <span style={{
              width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0,
              backgroundColor: e.is_current ? GOLD : (dark ? 'rgba(255,255,255,0.25)' : '#c9ccd2'),
            }} />
            {i < entries.length - 1 && (
              <span style={{ width: '1px', flex: 1, minHeight: '22px', backgroundColor: dark ? 'rgba(255,255,255,0.12)' : '#e4e7ec' }} />
            )}
          </div>
          <div style={{ minWidth: 0, paddingBottom: SP.sm }}>
            <div style={{ fontSize: FS.body, fontWeight: FW.semibold, color: t.tx }}>
              {e.value}
              {e.is_current && (
                <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: FW.semibold, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Current
                </span>
              )}
            </div>
            <div style={{ fontSize: FS.small, color: TEXT_MUTED, marginTop: '2px' }}>
              {LABEL[e.entry_type] || e.entry_type}
              {' · '}
              {fmt(e.start_date)}{' to '}{e.end_date ? fmt(e.end_date) : 'present'}
            </div>
            {e.note && (
              <div style={{ fontSize: FS.small, color: TEXT_FAINT, marginTop: '2px' }}>{e.note}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── RESPONSIVE Phase 1: window-width hook + horizontal-scroll table wrapper ──────
// The app styles exclusively with inline JS style objects, where @media queries cannot be
// expressed. These two primitives are the Phase-1 responsiveness layer: a hook that reports the
// live window width (so a layout can pick a style object per breakpoint) and a table wrapper that
// lets a wide table scroll horizontally instead of overflowing/crushing the viewport when narrowed.
// Both are invisible at normal desktop widths.

/** RESPONSIVE_BREAKPOINT: the single width (px) below which the desktop layout is considered
 *  "narrow" and the shell sidebar collapses. Defined once so every call site agrees. */
export const NARROW_BREAKPOINT = 900;

/** Live window width, updated on resize. Pure JS (no dependency). SSR-safe default (returns a wide
 *  value when window is unavailable so nothing collapses during a non-browser render). */
export function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    // Sync once on mount in case the initial state was the SSR default.
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

/** True when the window is at or below NARROW_BREAKPOINT (i.e. the shell should collapse). */
export function useIsNarrow(breakpoint = NARROW_BREAKPOINT) {
  return useWindowWidth() <= breakpoint;
}

/** RESPONSIVE Phase 2: gridCols(isNarrow, wide) returns the grid-template-columns to use - a single
 *  '1fr' column when narrow, else the given wide template. Lets a fixed multi-column grid (KPI rows,
 *  two-col form field rows) REFLOW to one column below the breakpoint instead of squeezing forever.
 *  A pure helper (not a hook), so it works at a JSX render site AND when spread over a module-level
 *  style object: `style={{ ...styles.row, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') }}`.
 *  Pass isNarrow from useIsNarrow(). Invisible at wide widths (returns the wide template unchanged). */
export function gridCols(isNarrow, wide) {
  return isNarrow ? '1fr' : wide;
}

/** ScrollableTable: wraps a wide <table> so it scrolls horizontally inside its own box instead of
 *  overflowing the page or squeezing its columns to nothing when the window is narrowed. At normal
 *  widths the wrapper is inert (the table is narrower than its container, so no scrollbar shows), so
 *  this is invisible on a wide desktop. Presentational only - it renders its children unchanged. */
export function ScrollableTable({ children, style }) {
  return (
    <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch', ...style }}>
      {children}
    </div>
  );
}

// ── RESPONSIVE Phase 3: the ONE shared, responsive sidebar for every shell ──────────
// Consolidates the previously-forked sidebar render loops (COO Dashboard, FinanceView/payroll,
// ComplianceView, EmployeeView) into one component. It is deliberately FLEXIBLE so each shell keeps
// its exact behaviour byte-for-byte - it owns only the shared plumbing (the outer container, the
// profile slot, the section/item map, the responsive width) and takes everything shell-specific as
// props: the style tokens (itemStyle/itemActiveStyle/sectionLabelStyle), the click handler
// (onSelect, so a shell that clears extra state on nav still does), the badge renderer, and any
// appended block. StaffView is intentionally NOT a caller - it is a topbar-only layout with no
// sidebar. Presentational only; no data fetching, routing, or gating lives here.
//
// Modes: pass `sections` ([{label, items}]) for a grouped sidebar, OR `items` ([...]) for a flat
// one (EmployeeView). `showItemLabelSpan` wraps the label in <span style={{flex:1}}> (the grouped
// shells) vs rendering it bare (EmployeeView's exact markup).
export function Sidebar({
  width, containerStyle, profile, dividerAfterProfile,
  sections, items, appended,
  activeSection, onSelect,
  itemStyle, itemActiveStyle, sectionLabelStyle,
  renderBadge, showItemLabelSpan = true,
}) {
  const renderItem = (item) => (
    <button
      key={item.id}
      style={activeSection === item.id ? itemActiveStyle : itemStyle}
      onClick={() => onSelect(item)}
    >
      {item.icon && <span style={{ flexShrink: 0, display: 'flex' }}>{item.icon}</span>}
      {showItemLabelSpan ? <span style={{ flex: 1 }}>{item.label}</span> : item.label}
      {renderBadge ? renderBadge(item) : null}
    </button>
  );

  return (
    <div style={{ ...containerStyle, width, flexShrink: 0 }}>
      {profile}
      {profile && dividerAfterProfile}
      {sections
        ? sections.map((section) => (
            <div key={section.label}>
              <p style={sectionLabelStyle}>{section.label}</p>
              {section.items.map(renderItem)}
            </div>
          ))
        : (items || []).map(renderItem)}
      {appended}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────────────────────
// ARCHREPLACE: archiving a CONTRACT or a CV, on the employee's OWN record, collects the
// replacement file in the SAME action.
// ─────────────────────────────────────────────────────────────────────────────────────────────
//
// WHY A DEDICATED MODAL RATHER THAN EXTENDING ConfirmModal: ConfirmModal is deliberately
// presentational and message-only - it renders a string and two buttons, with no slot for a
// control. Growing it a children/extra-content prop would make every one of its ~10 existing call
// sites carry a concept only this one needs. This is its own small component in the same file,
// following the same overlay geometry (fixed rgba(0,0,0,0.5) backdrop, centred 12-radius card,
// role="dialog" aria-modal, backdrop click cancels, shared Btn with the danger variant), so the
// two dialogs are visually indistinguishable to the user.
//
// The four OTHER self-archivable types (passport, visa, proof_of_address, p45) keep the plain
// ConfirmModal, unchanged, and coo/admin keep it too - see each call site.

/** Types whose archive action requires the replacement in the same step, for an employee acting
 *  on their OWN record. MIRRORS the backend's REPLACEMENT_REQUIRED_TYPES (documents.py) - keep in
 *  sync if that set changes. Deliberately a SUBSET of MANUAL_ARCHIVE_TYPES: it narrows how an
 *  already-permitted archive is performed, it never widens what may be archived. */
export const REPLACEMENT_REQUIRED_TYPES = new Set(['contract', 'cv']);

/** Client-side mirrors of the two upload rules the server enforces, used ONLY to tell the user
 *  sooner. The server is still the real gate on every one of them (documents.py's
 *  is_blocked_upload_filename and MAX_UPLOAD_BYTES), and its refusal is what the call sites
 *  surface when one slips through - these just avoid a pointless round trip. */
export const BLOCKED_UPLOAD_EXTENSIONS = [
  '.html', '.htm', '.svg', '.js', '.php', '.exe', '.sh', '.bat', '.cmd',
  '.php5', '.phtml', '.jsp', '.svgz', '.xhtml', '.shtml', '.mhtml',
];
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** DOCREFUSAL: why the file cannot be used, as {code, ext}, or null when it looks acceptable. The
 *  code is one of the server's reason codes, so the browser and the server name a refusal the same
 *  way. Same parse as the backend's is_blocked_upload_filename: trailing dots and whitespace are
 *  stripped first (so `evil.exe.` resolves to `.exe`) and the LAST segment is matched (so
 *  `report.pdf.exe` is caught). */
export function replacementFileProblemCode(file) {
  if (!file) return { code: 'no_file' };
  const cleaned = String(file.name || '').trim().replace(/[.\s\u0000]+$/, '');
  if (cleaned.includes('.')) {
    const ext = '.' + cleaned.split('.').pop().toLowerCase().trim();
    if (BLOCKED_UPLOAD_EXTENSIONS.includes(ext)) return { code: 'file_type_blocked', ext };
  }
  if (file.size > MAX_UPLOAD_BYTES) return { code: 'file_too_large' };
  if (file.size === 0) return { code: 'file_empty' };
  return null;
}

const PROBLEM_KEYS = {
  no_file: 'archiveReplace.problemNoFile',
  file_type_blocked: 'archiveReplace.problemBlocked',
  file_too_large: 'archiveReplace.problemTooLarge',
  file_empty: 'archiveReplace.problemEmpty',
};

/** The reason, in `lang` (English by default), or null when the file looks acceptable. */
export function replacementFileProblem(file, lang = 'en') {
  const p = replacementFileProblemCode(file);
  return p ? docText(PROBLEM_KEYS[p.code], lang, { ext: p.ext || '' }) : null;
}

/** ARCHREPLACE: the archive-and-replace dialog. Confirm stays DISABLED until a file that passes
 *  the client-side mirrors above is chosen, and the reason is shown inline the moment a rejected
 *  file is picked, so the user is told BEFORE submitting rather than by a toast afterwards. */
export function ArchiveReplaceModal({ open, dark, typeLabel, documentType, busy, onConfirm, onCancel }) {
  const [file, setFile] = useState(null);
  const [problem, setProblem] = useState(null);
  // DOCREFUSAL point 7: every word of this dialog comes from the locale files, in the viewer's
  // language. The document's own name is the caller's label in English and the French type name
  // in French.
  const lang = useDocLang();
  const tr = (key, vars) => docText(key, lang, vars);
  // A11YLABEL: from useId for the same reason Field uses it - this modal is a shared component
  // and a fixed id would collide if two ever rendered at once.
  const replaceInputId = `replace-file-${useId()}`;
  useEffect(() => { if (!open) { setFile(null); setProblem(null); } }, [open]);
  if (!open) return null;
  const pick = (chosen) => {
    const found = chosen ? replacementFileProblemCode(chosen) : null;
    // DOCREFUSAL point 10: the instant message stays, and the refusal is also reported, so a file
    // refused in the browser leaves the same trace as one refused by the server.
    if (found) reportBrowserRefusal(found.code, documentType, 'archive');
    setProblem(found ? tr(PROBLEM_KEYS[found.code], { ext: found.ext || '' }) : null);
    setFile(found ? null : chosen);
  };
  const frLabel = documentType ? docText(`docUpload.docTypes.${documentType}`, 'fr') : '';
  const label = (lang && String(lang).toLowerCase().startsWith('fr') && frLabel
                 && frLabel !== `docUpload.docTypes.${documentType}`)
    ? frLabel.toLowerCase()
    : (typeLabel || tr('archiveReplace.documentFallback'));
  return (
    <div
      onClick={busy ? undefined : onCancel}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label={tr('archiveReplace.title', { label })}
        style={{
          backgroundColor: dark ? '#131c2e' : '#fff',
          color: dark ? '#eaecf0' : '#1a1a2e',
          borderRadius: '12px', padding: '24px', width: '460px', maxWidth: 'calc(100vw - 48px)',
          boxShadow: '0 12px 16px -4px rgba(16,24,40,.18), 0 4px 6px -2px rgba(16,24,40,.08)',
          border: dark ? '1px solid #25324c' : 'none',
        }}
      >
        <h2 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 600 }}>
          {tr('archiveReplace.title', { label })}
        </h2>
        <p style={{ margin: '0 0 16px 0', fontSize: FS.body, lineHeight: 1.5, color: dark ? '#b6bdc9' : '#444' }}>
          {tr('archiveReplace.body', { label })}
        </p>
        <label style={{ display: 'block', fontSize: FS.body, fontWeight: 500, marginBottom: '6px' }} htmlFor={replaceInputId}>
          {tr('archiveReplace.newFile', { label })}
        </label>
        <input id={replaceInputId}
          type="file"
          aria-label={tr('archiveReplace.newFile', { label })}
          disabled={busy}
          onChange={(e) => pick(e.target.files && e.target.files[0])}
          style={{ display: 'block', width: '100%', marginBottom: problem ? '8px' : '20px', fontSize: FS.body }}
        />
        {problem && (
          <p role="alert" style={{ margin: '0 0 20px 0', fontSize: FS.body, color: DANGER }}>
            {problem}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SP.sm }}>
          <Btn variant="secondary" dark={dark} disabled={busy} onClick={onCancel}>{tr('archiveReplace.cancel')}</Btn>
          <Btn variant="danger" dark={dark} disabled={busy || !file}
               onClick={() => file && onConfirm(file)}>
            {busy ? tr('archiveReplace.busy') : tr('archiveReplace.confirm')}
          </Btn>
        </div>
      </div>
    </div>
  );
}
